import { z } from 'zod';
export const communicationsChannelSchema = z.enum(['email', 'sms']);
export const communicationsMessageStateSchema = z.enum([
    'queued', 'sent', 'delivered', 'failed', 'suppressed',
]);
export const communicationsMessageSourceReferenceSchema = z.strictObject({
    capability: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/),
    resourceType: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
    resourceId: z.string().uuid(),
    purpose: z.string().regex(/^[a-z][a-z0-9-]{0,79}$/),
});
export const communicationsMessageSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    threadId: z.string().uuid(),
    direction: z.enum(['outbound', 'inbound']),
    state: communicationsMessageStateSchema,
    body: z.string().min(1).max(10_000),
    // The resolved destination is intentionally absent from public responses:
    // history readers see the contact, not raw addresses.
    suppressedReason: z.enum(['not-authorized-or-unreachable']).nullable(),
    sourceReference: communicationsMessageSourceReferenceSchema.nullable(),
    revision: z.number().int().positive(),
    sentAt: z.string().datetime({ offset: true }).nullable(),
    deliveredAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if (['sent', 'delivered'].includes(value.state) && value.sentAt === null) {
        context.addIssue({ code: 'custom', path: ['sentAt'], message: 'Sent messages require sentAt.' });
    }
    if ((value.state === 'delivered') !== (value.deliveredAt !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['deliveredAt'],
            message: 'Only delivered messages carry deliveredAt.',
        });
    }
});
export const communicationsThreadSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    channel: communicationsChannelSchema,
    contactId: z.string().uuid(),
    projectId: z.string().uuid().nullable(),
    subject: z.string().max(240).nullable(),
    messageCount: z.number().int().nonnegative(),
    lastMessageAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const communicationsSendMessageSchema = z.strictObject({
    channel: communicationsChannelSchema,
    contactId: z.string().uuid(),
    projectId: z.string().uuid().nullable().default(null),
    subject: z.string().trim().min(1).max(240).nullable().default(null),
    body: z.string().trim().min(1).max(10_000),
});
export const communicationsThreadListQuerySchema = z.strictObject({
    contactId: z.string().uuid().optional(),
    projectId: z.string().uuid().optional(),
    channel: communicationsChannelSchema.optional(),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
});
export const communicationsMessageResponseSchema = z.strictObject({
    data: communicationsMessageSchema,
});
export const communicationsThreadsResponseSchema = z.strictObject({
    data: z.array(communicationsThreadSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const communicationsThreadMessagesResponseSchema = z.strictObject({
    data: z.strictObject({
        thread: communicationsThreadSchema,
        messages: z.array(communicationsMessageSchema),
    }),
});
//# sourceMappingURL=communications-message.contract.js.map